const setInitialised = () => {
    /** @param {KeenSliderInstance} carousel */
    return (carousel) => {
        carousel.on('created', (carousel) => {
            carousel.container.setAttribute(`data-initialised`, true)
        })
    }
}

/**
 * @param {{
 *  container?: HTMLElement,
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} elements
 */
const useArrows = ({ container, next, previous } = {}) => {
    /** @param {KeenSliderInstance} carousel */
    return (carousel) => {
        next?.addEventListener('click', carousel.next)
        previous?.addEventListener('click', carousel.prev)

        const updateButtonStates = () => {
            const { abs, maxIdx, min } = carousel.track.details

            container?.setAttribute('aria-hidden', maxIdx === 0)

            if (next) {
                next.disabled = abs === maxIdx
                next.setAttribute('aria-hidden', maxIdx === 0)
            }

            if (previous) {
                previous.disabled = abs === min
                previous.setAttribute('aria-hidden', maxIdx === 0)
            }
        }

        carousel.on('created', updateButtonStates)
        carousel.on('slideChanged', updateButtonStates)
    }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 * }} options
 */
const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
    const delay = seconds * 1000

    /** @param {KeenSliderInstance} carousel */
    return (carousel) => {
        let timeout
        let mouseOver = false

        const clearCurrentTimout = () => clearTimeout(timeout)
        const setNextTimeout = () => {
            clearCurrentTimout()

            if (mouseOver) {
                return
            }

            timeout = setTimeout(carousel.next, delay)
        }

        carousel.on('created', () => {
            if (pauseOnHover) {
                carousel.container.addEventListener('mouseover', () => {
                    mouseOver = true
                    clearCurrentTimout()
                })
                carousel.container.addEventListener('mouseout', () => {
                    mouseOver = false
                    setNextTimeout()
                })
            }

            setNextTimeout()
        })

        carousel.on('dragStarted', clearCurrentTimout)
        carousel.on('animationEnded', setNextTimeout)
        carousel.on('updated', setNextTimeout)
    }
}

/**
 * @param {{
 *  counter?: HTMLElement,
 * }} counter
 */
const useCounter = ({ counter } = {}) => {
    /** @param {KeenSliderInstance} carousel */
    return (carousel) => {
        const updateCounterState = () => {
            const maximumIndex = parseInt(carousel.track.details.maxIdx)
            const currentIndex = parseInt(carousel.track.details.rel)

            const slidesPerView = parseInt(carousel.options.slides.perView)

            counter?.setAttribute('aria-hidden', maximumIndex === 0)

            if (counter) {
                counter.innerText = `${currentIndex + slidesPerView}/${maximumIndex + slidesPerView}`
            }
        }

        carousel.on('created', updateCounterState)
        carousel.on('slideChanged', updateCounterState)
    }
}

/** @param {HTMLButtonElement[]} dots */
const useDots = (dots = []) => {
    /** @param {KeenSliderInstance} carousel */
    return (carousel) => {
        const updateDotStates = () => {
            dots.forEach((dot, index) => {
                const isCurrent = carousel.track.details.rel === index

                dot.setAttribute('aria-current', String(isCurrent))
            })
        }

        dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
                carousel.moveToIdx(index)
            })
        })

        carousel.on('created', updateDotStates)
        carousel.on('slideChanged', updateDotStates)
    }
}

/**
 * @param {{
 *  container?: HTMLElement,
 *  progressbar?: HTMLElement,
 * }} progress
 */
const useProgress = ({ container, progressbar } = {}) => {
    /** @param {KeenSliderInstance} carousel */
    return (carousel) => {
        const updateProgressState = () => {
            const maximumIndex = carousel.track.details.maxIdx
            const currentIndex = carousel.track.details.rel

            container?.setAttribute('aria-hidden', maximumIndex === 0)

            const barLength = 100 / (maximumIndex + 1)
            const barPositon = (currentIndex / (maximumIndex + 1)) * 100

            progressbar?.style.setProperty('--Bar_Width', `${barLength}%`)
            progressbar?.style.setProperty('--Bar_Position', `${barPositon}%`)
        }

        carousel.on('created', updateProgressState)
        carousel.on('slideChanged', updateProgressState)
    }
}

window.PURELEI.Carousel = window.PURELEI.Carousel || {}
window.PURELEI.Carousel.plugins = {
    setInitialised,
    useArrows,
    useAutoplay,
    useCounter,
    useDots,
    useProgress,
}
